import { useState, useEffect, useRef } from "react";
import "./Grade1MathLesson.css";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import ProgressBar from "../MathPagesStructure/ProgressBar.js";
import { StoryModeQuiz, QuizResultScreen, FinalQuizUI } from "../MathPagesStructure/StoryModeStructure";
import LessonUI from "../MathPagesStructure/LessonUI";
import { mathChapterPages, GRADE } from "../MathLessonPools";
import { generateQuizQuestions, generateFinalQuizQuestions } from "./Grade1QuizGenerator.js";
import { doc, onSnapshot, runTransaction, serverTimestamp} from "firebase/firestore";
import { auth, db } from "../../firebase";

// --- 3. Main Component ---
const COURSE_ID = "math_g1";
const PASS_PERCENT = 80;           // >= 80 % 才加 token
const FINAL_TIME_SEC = 300;        // 5 分鐘

export default function Grade1MathLesson({ onAwardTokens }) {
  /* ---------- Lesson / Quiz 狀態 ---------- */
  const [pageIdx, setPageIdx]         = useState(0);

  const [showQuickQuiz, setShowQuickQuiz] = useState(false);
  const [quiz, setQuiz]               = useState([]);
  const [quizIdx, setQuizIdx]         = useState(0);
  const [quizScore, setQuizScore]     = useState(0);
  const [quizFinished, setQuizFinished] = useState(false);

  const [showFinal, setShowFinal]     = useState(false);
  const [finalQuiz, setFinalQuiz]     = useState([]);
  const [finalIdx, setFinalIdx]       = useState(0);
  const [finalScore, setFinalScore]   = useState(0);
  const [finalFinished, setFinalFinished] = useState(false);
  const [finalTime, setFinalTime]     = useState(FINAL_TIME_SEC);

  /* ---------- User / 進度 ---------- */
  const [user, setUser]               = useState(null);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  const hasAwardedTokens = useRef(false);

  /* 1️⃣ 即時監聽本人 user doc */
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const unsub = onSnapshot(doc(db, "users", uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setUser(data);

      if (data.progress?.[COURSE_ID]?.completed) {
        setHasCompletedBefore(true);
      }
    });

    return unsub;
  }, []);

/* ---------------- 2️⃣ Final 倒數計時 ---------------- */
useEffect(() => {
  let alive = true;                       // ← 新增旗標，防止元件 unmount 後再 setState

  if (showFinal && !finalFinished && finalTime > 0) {
    const t = setTimeout(() => {
      if (alive) setFinalTime(s => s - 1);
    }, 1000);

    return () => {                        // ← 離開時同時：
      alive = false;                      //    1) 取消後續 setState
      clearTimeout(t);                    //    2) 清除 timer
    };
  } else if (finalTime === 0) {
    setFinalFinished(true);
  }
}, [showFinal, finalFinished, finalTime]);

/* ---------------- 3️⃣ 完成 Final Quiz → 寫進度 / 加 token ---------------- */
const [saveError, setSaveError] = useState(null);  // ← 顯示錯誤用

useEffect(() => {
  if (!finalFinished || hasAwardedTokens.current) return;

  (async () => {
    const passed = (finalScore / finalQuiz.length) * 100 >= PASS_PERCENT;
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {                                          // ← 包進 try/catch
      await runTransaction(db, async (tx) => {
        const userRef = doc(db, "users", uid);
        const snap = await tx.get(userRef);
        const data = snap.data() || {};

        const progress = {
          ...(data.progress || {}),
          [COURSE_ID]: {
            completed: true,
            finalQuizScore: finalScore,
            finalQuizTotal: finalQuiz.length,
            lastCompleted: serverTimestamp(),
          },
        };

        const update = { progress };
        if (passed) update.coins = (data.coins ?? 0) + 1;
        tx.set(userRef, update, { merge: true });
      });

      if (passed && onAwardTokens) onAwardTokens(1);
      hasAwardedTokens.current = true;
      setSaveError(null);                           // ← 清除舊錯誤
    } catch (err) {
      console.error("Save progress failed:", err);
      setSaveError("⚠️  無法儲存進度，請檢查網路後再試一次！");
    }
  })();
}, [finalFinished, finalScore, finalQuiz.length, onAwardTokens]);

  /* ---------- Quick Quiz 控制 ---------- */
  const startQuickQuiz = () => {
    setShowQuickQuiz(true);
    setQuiz(generateQuizQuestions(10));
    setQuizIdx(0);
    setQuizScore(0);
    setQuizFinished(false);
  };

  const handleQuizAnswer = (opt) => {
    if (opt === quiz[quizIdx].correctAnswer) setQuizScore((s) => s + 1);
    if (quizIdx < quiz.length - 1) setQuizIdx((i) => i + 1);
    else setQuizFinished(true);
  };

  const handleReturnToLesson = () => {
    setShowQuickQuiz(false);
    setPageIdx(0);
  };

  /* ---------- Final Quiz 控制 ---------- */
  const handleStartFinalQuiz = () => {
    setShowQuickQuiz(false);
    setFinalQuiz(generateFinalQuizQuestions(50));
    setShowFinal(true);
    setFinalIdx(0);
    setFinalScore(0);
    setFinalFinished(false);
    setFinalTime(FINAL_TIME_SEC);
    hasAwardedTokens.current = false;
  };

  const handleFinalQuizAnswer = (opt) => {
    if (opt === finalQuiz[finalIdx].correctAnswer) setFinalScore((s) => s + 1);
    if (finalIdx < finalQuiz.length - 1) setFinalIdx((i) => i + 1);
    else setFinalFinished(true);
  };

  const handleReturnFromFinal = () => {
    setShowFinal(false);
    setPageIdx(0);
  };

  /* ---------- 進度條 ---------- */
  const totalSlides = mathChapterPages[GRADE].length;
  const progress = Math.round(((pageIdx + 1) / totalSlides) * 100);

  /* ---------- Render ---------- */
  return (
    <div className="grade1-math-page">
      <div className="grade1-math-page-scale">
        {saveError && <p className="error-msg">{saveError}</p>}
        <ProgressBar progress={progress} />

        {hasCompletedBefore && !showFinal && !showQuickQuiz && (
          <div style={{ textAlign: "center", margin: "10px 0", color: "#555" }}>
            <p>
              You previously finished Grade&nbsp;1. Feel free to review or
              retake the final quiz!
            </p>
            <button className="button1" onClick={() => setPageIdx(0)}>
              Review Lesson
            </button>
            <button className="button1" onClick={handleStartFinalQuiz}>
              Retake Final Quiz
            </button>
          </div>
        )}

        {!showQuickQuiz && !showFinal && (
          <LessonUI
            pages={mathChapterPages[GRADE]}
            currentPageIndex={pageIdx}
            onPrevPage={() => setPageIdx((p) => p - 1)}
            onNextPage={() => setPageIdx((p) => p + 1)}
            onStartQuiz={startQuickQuiz}
            backTo="/math"
          />
        )}

        {showQuickQuiz && !quizFinished && (
          <StoryModeQuiz
            questions={quiz}
            currentIndex={quizIdx}
            score={quizScore}
            user={user}
            onAnswer={handleQuizAnswer}
            studentAvatar={user?.avatar ?? defaultProfile}
          />
        )}
        {showQuickQuiz && quizFinished && (
          <QuizResultScreen
            score={quizScore}
            total={quiz.length}
            onContinue={handleStartFinalQuiz}
            onRetry={handleReturnToLesson}
            continueLabel="Start Final Quiz"
            retryLabel="Return to Lesson"
          />
        )}

        {showFinal && (
          <FinalQuizUI
            questions={finalQuiz}
            currentIndex={finalIdx}
            score={finalScore}
            timeLeft={finalTime}
            onAnswer={handleFinalQuizAnswer}
            quizFinished={finalFinished}
            onReturn={handleReturnFromFinal}
          />
        )}
      </div>
    </div>
  );
}
